import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Loader2, CheckCircle2, AlertTriangle, Clock, Lock } from "lucide-react";
import api from "@/lib/api";
import { inputCls as inp } from "@/components/tournament/constants";

async function compress(file) {
  if (!file.type.startsWith("image/")) throw new Error("Please choose an image");
  const img = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.8));
  if (blob.size > 5 * 1024 * 1024) throw new Error("Photo is still larger than 5MB");
  return new File([blob], "proof.jpg", { type: "image/jpeg" });
}

const scoreOptions = (bo, allowDraw) => {
  const win = Math.floor(bo / 2) + 1;
  const out = [];
  for (let l = 0; l < win; l++) out.push([win, l]);
  for (let l = win - 1; l >= 0; l--) out.push([l, win]);
  if (allowDraw) out.push([1, 1]);
  return out;
};

export default function ReportResult() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ reporter: "", winner: "", score: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get(`/report/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(e.response?.data?.detail || "Invalid QR code")); }, [token]);

  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const c = await compress(file); setPhoto(c); setPreview(URL.createObjectURL(c)); } catch (x) { setErr(x.message); }
  };
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const [sa, sb] = f.score.split("-").map(Number);
    const fd = new FormData();
    fd.append("reporter", f.reporter); fd.append("winner", f.winner); fd.append("sa", sa); fd.append("sb", sb);
    if (photo) fd.append("photo", photo);
    setBusy(true);
    try { const r = await api.post(`/report/${token}`, fd); setDone(r.data); } catch (x) { setErr(x.response?.data?.detail || "Submission failed — check your connection and try again"); } finally { setBusy(false); }
  };

  if (err && !info) return <div className="py-24 px-4 text-center" data-testid="report-invalid"><Lock className="mx-auto text-white/30" size={36} /><h1 className="mt-4 text-2xl font-bold text-white">{err}</h1></div>;
  if (!info) return <div className="py-24 text-center text-white/40">Loading…</div>;
  if (done) {
    const Icon = done.status === "confirmed" ? CheckCircle2 : done.status === "disputed" ? AlertTriangle : Clock;
    return <div className="py-24 px-4 text-center max-w-md mx-auto" data-testid="report-done"><Icon size={44} className={`mx-auto ${done.status === "confirmed" ? "text-green-400" : done.status === "disputed" ? "text-amber-400" : "text-white/60"}`} /><h1 className="mt-4 text-2xl font-bold text-white" data-testid="report-done-status">{done.status === "confirmed" ? "Result confirmed" : done.status === "disputed" ? "Result disputed" : "Report received"}</h1><p className="mt-2 text-white/60">{done.message}</p></div>;
  }
  const [a, b] = info.teams;
  const opts = scoreOptions(info.best_of, info.allow_draw);
  const valid = (sc) => { if (!f.winner || !sc) return true; const [x, y] = sc.split("-").map(Number); return f.winner === "draw" ? x === y : f.winner === a ? x > y : y > x; };

  return (
    <div className="mx-auto max-w-lg px-4 py-10" data-testid="report-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card-surface rounded-2xl p-6">
        <div className="eyebrow text-white/40">{info.game} · {info.tournament}</div>
        <h1 className="mt-1 text-2xl font-bold text-white">Submit Match Result</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div><label className="block text-xs font-semibold text-white/50 mb-1.5">Match</label><input readOnly value={`${info.match} · ${a} vs ${b}`} className={`${inp} opacity-70 cursor-not-allowed`} data-testid="report-match" /></div>
          <div><label className="block text-xs font-semibold text-white/50 mb-1.5">Reporting team (captain)</label>
            <select required value={f.reporter} onChange={(e) => setF({ ...f, reporter: e.target.value })} className={inp} data-testid="report-reporter"><option value="">Select your team</option>{info.teams.map((tm) => <option key={tm} disabled={info.reported_by.includes(tm)}>{tm}{info.reported_by.includes(tm) ? " (already reported)" : ""}</option>)}</select></div>
          <div><label className="block text-xs font-semibold text-white/50 mb-1.5">Winner</label>
            <select required value={f.winner} onChange={(e) => setF({ ...f, winner: e.target.value, score: "" })} className={inp} data-testid="report-winner"><option value="">Select winner</option>{info.teams.map((tm) => <option key={tm}>{tm}</option>)}{info.allow_draw && <option value="draw">Draw</option>}</select></div>
          <div><label className="block text-xs font-semibold text-white/50 mb-1.5">Final score ({a} – {b}) · {info.format}</label>
            <select required value={f.score} onChange={(e) => setF({ ...f, score: e.target.value })} className={inp} data-testid="report-score"><option value="">Select score</option>{opts.map(([x, y]) => { const v = `${x}-${y}`; return <option key={v} value={v} disabled={!valid(v)}>{x} – {y}</option>; })}</select></div>
          <div><label className="block text-xs font-semibold text-white/50 mb-1.5">Proof photo (result screen)</label>
            <label className="relative flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-black/30 aspect-video overflow-hidden cursor-pointer" data-testid="report-photo">
              {preview ? <img src={preview} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <><Camera className="text-white/40" /><span className="text-xs text-white/50">Take or choose a photo · compressed automatically (max 5MB)</span></>}
              <input type="file" accept="image/*" capture="environment" hidden onChange={pick} data-testid="report-photo-input" />
            </label></div>
          {err && <div className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2" data-testid="report-error">{err}</div>}
          <button disabled={busy} data-testid="report-submit" className="w-full py-3 rounded-full bg-[hsl(var(--primary))] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 size={16} className="animate-spin" />} Submit Result</button>
          <p className="text-[11px] text-white/30 text-center">Submission time and device are logged to help resolve disputes.</p>
        </form>
      </motion.div>
    </div>
  );
}
